import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { AiChatService } from '../../api/services/ai-chat.service';
import { InvoicePeerService } from '../../service/invoice-peer.service';
import { AiChatSseService } from '../../service/ai-chat-sse.service';
import { UserService } from '../../service/user.service';
import { ChatModel, ChatSession, ChatMessage } from '../../api/models';
import { Subject, combineLatest, of } from 'rxjs';
import { takeUntil, finalize, map, catchError } from 'rxjs/operators';

@Component({
  selector: 'app-invoice-ai-chat-widget',
  templateUrl: './invoice-ai-chat-widget.component.html',
  styleUrls: ['./invoice-ai-chat-widget.component.scss']
})
export class InvoiceAiChatWidgetComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('scrollContainer') private scrollContainer: ElementRef;

  public isOpen = false;
  public showHistory = false;
  public invoiceId: number;
  public invoiceNumber: string;
  public currentUserId: number | null = null;

  public models: ChatModel[] = [];
  public selectedModelId: string;
  public activeSession: ChatSession | null = null;
  public messages: ChatMessage[] = [];
  public sessionHistory: ChatSession[] = [];

  public currentInput = '';
  public isGenerating = false;
  public currentStreamedText = '';
  public toolUsageStatus = '';

  private destroy$ = new Subject<void>();

  constructor(
    private aiChatService: AiChatService,
    private invoicePeerService: InvoicePeerService,
    private aiChatSseService: AiChatSseService,
    public userService: UserService
  ) {}

  ngOnInit(): void {
    // AI: Gate API calls behind role check to prevent 403 errors for non-AI users.
    combineLatest([
      this.invoicePeerService.getInvoice(),
      this.userService.hasAIChatAccess$,
      this.userService.user$
    ]).pipe(
      takeUntil(this.destroy$)
    ).subscribe(([invoice, hasAccess, userState]) => {
      if (invoice) {
        this.invoiceId = invoice.id;
        this.invoiceNumber = invoice.number;
      }
      this.currentUserId = userState.userId;
      if (invoice && hasAccess) {
        this.loadModels();
        this.loadRecentSession();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewChecked(): void {
    if (!this.showHistory) {
      this.scrollToBottom();
    }
  }

  public toggleChat(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen && this.messages.length === 0 && this.invoiceId) {
      this.loadRecentSession();
    }
  }

  public toggleHistory(): void {
    this.showHistory = !this.showHistory;
    if (this.showHistory) {
      this.loadSessionHistory();
    }
  }

  public get isViewOnly(): boolean {
    if (!this.activeSession || this.currentUserId === null) return false;
    return this.activeSession.created_by !== this.currentUserId;
  }

  private loadModels(): void {
    this.aiChatService.listChatModels().pipe(
      takeUntil(this.destroy$)
    ).subscribe(resp => {
      this.models = resp.models || [];
      if (this.models.length > 0 && !this.selectedModelId) {
        const available = this.models.find(m => m.available);
        if (available) {
          this.selectedModelId = available.id;
        }
      }
    });
  }

  private loadRecentSession(): void {
    if (!this.invoiceId) return;

    this.aiChatService.listChatSessions({ id: this.invoiceId }).pipe(
      takeUntil(this.destroy$),
      map(sessions => sessions.find(s => s.created_by === this.currentUserId))
    ).subscribe(session => {
      if (session) {
        this.loadSession(session.id);
      }
    });
  }

  private loadSessionHistory(): void {
    if (!this.invoiceId) return;

    this.aiChatService.listChatSessions({ id: this.invoiceId }).pipe(
      takeUntil(this.destroy$)
    ).subscribe(sessions => {
      this.sessionHistory = sessions || [];
    });
  }

  public selectHistorySession(session: ChatSession): void {
    this.showHistory = false;
    this.loadSession(session.id);
  }

  public startNewChat(): void {
    this.activeSession = null;
    this.messages = [];
    this.currentStreamedText = '';
    this.toolUsageStatus = '';
    this.showHistory = false;
  }

  private loadSession(sessionId: string): void {
    this.aiChatService.getChatSession({ id: this.invoiceId, sessionId }).pipe(
      takeUntil(this.destroy$)
    ).subscribe(resp => {
      this.activeSession = resp.session;
      this.messages = resp.messages || [];
      this.selectedModelId = resp.session.model_id;
    });
  }

  public sendMessage(): void {
    if (!this.currentInput.trim() || this.isGenerating || this.isViewOnly) return;
    if (!this.selectedModelId && !this.activeSession) return;

    const content = this.currentInput;
    this.currentInput = '';
    this.isGenerating = true;
    this.currentStreamedText = '';
    this.toolUsageStatus = '';

    if (!this.activeSession) {
      this.aiChatService.createChatSession({
        id: this.invoiceId,
        body: { model_id: this.selectedModelId }
      }).pipe(
        takeUntil(this.destroy$),
        catchError(err => {
          this.isGenerating = false;
          console.error('Failed to create session', err);
          return of(null);
        })
      ).subscribe(session => {
        if (session) {
          this.activeSession = session;
          this.performSendMessage(session.id, content);
        }
      });
    } else {
      this.performSendMessage(this.activeSession.id, content);
    }
  }

  private performSendMessage(sessionId: string, content: string): void {
    // AI: Manually add user message to UI immediately
    const userMsg: ChatMessage = {
      id: 'temp-' + Date.now(),
      session_id: sessionId,
      role: 'user',
      content: content,
      created_at: new Date().toISOString()
    };
    this.messages.push(userMsg);

    this.aiChatSseService.sendMessageStream(this.invoiceId, sessionId, content).pipe(
      takeUntil(this.destroy$),
      finalize(() => {
        this.isGenerating = false;
        this.toolUsageStatus = '';
      })
    ).subscribe({
      next: (event) => {
        switch (event.type) {
          case 'content-delta':
            this.currentStreamedText += event.data.text;
            break;
          case 'tool-call':
            this.toolUsageStatus = event.data.status;
            break;
          case 'message-end':
            // AI: Replace streamed text with final persisted message
            this.currentStreamedText = '';
            this.messages.push(event.data);
            break;
          case 'error':
            console.error('AI Error:', event.data.message);
            this.toolUsageStatus = 'Error: ' + event.data.message;
            break;
        }
      },
      error: (err) => {
        console.error('Stream error', err);
        this.isGenerating = false;
      }
    });
  }

  // ngAfterViewChecked calls this on every check, but the message list is
  // rendered only while the chat panel is open, so skip it when it is absent.
  private scrollToBottom(): void {
    const list = this.scrollContainer?.nativeElement;
    if (list) {
      list.scrollTop = list.scrollHeight;
    }
  }
}
